import { ReactSVG } from 'react-svg';
import Head from 'next/head';
import Link from 'next/link';
import { request } from 'graphql-request';
import key from 'weak-key';
import dynamic from 'next/dynamic';
import { useAmp } from 'next/amp';
import BodyParser from '~/components/bodyParser/bodyParser';
import BlockParser from '~/components/blockParser/blockParser';
import PageWrapper from '~/components/wrappers/pageWrapper';
import ParallaxHeader from '~/components/parallaxHeader/parallaxHeader';
import Share from '~/components/share/share';
import SiteSettings from '~/siteSettings';
import Stick from '~/components/stick/stick';
import Category from '~/components/category/category';
import Skimlinks from '~/components/skimlinks/skimlinks';
import Trackonomics from '~/components/trackonomics/trackonomics';
import VideoPlayerWithStore from '~/components/videoPlayerWithStore/videoPlayerWithStore';
import CarouselRecomendations from '~/components/carouselRecommendations/carouselRecommendations';
import TaxLinks from '~/components/taxLinks/taxLinks';
import {
  constructPlaylist,
  get,
  getAlias,
  getBlocksFromQueue,
  getContentPaths,
  getDate,
  getDrupalSettings,
  getIcon,
  getRecommendations,
  getStructuredData,
  has
} from '~/utils/utils';

// GQL
import GET_ARTICLE from '~/graphql/GET_ARTICLE.graphql';

// AMP
import ArticleAmp from './articleAmp';

const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });
const InjectedStylesCatcher = dynamic(() => import('~/components/injectedStylesCatcher/injectedStylesCatcher'));
const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });

/**
 * Content
 * @category Page
 * @extends Component
 * @summary Display the Content page.
 * @return {Node} React node containing the Content page.
 */

const Content = props => {
  const isAmp = useAmp();
  const {
    baseUrl,
    blocks,
    data,
    drupalSettings,
    fileUrl,
    recommendations,
    router,
    sitePrimaryRsid
  } = props;
  const path = get(router, 'query.alias')
    ? `/articles/${ router.query.alias.join('/') }`
    : '/';

  const content = {
    ...data.nodeQuery
  };

  const keywords = content.field_tags && content.field_tags.map(tag => tag.name).join(',');
  const playlist = constructPlaylist(content, recommendations);
  const blockItems = blocks?.entitySubQueueQuery?.items;

  return (
    <>
      <Head>
        <title>{ content.title } | { SiteSettings.siteName }</title>
        <meta name='title' content={ content.title } />
        <link rel='canonical' href={`${ baseUrl }${ path }`} />
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:title' content={ content.field_social_headline || content.title } />
        <meta name='description' content={ content.field_seo_description || content.field_subhead } />
        <meta property='og:url' content={ path } />
        <meta property='og:title' content={ content.field_social_headline || content.title } />
        <meta property='og:image' content={ content?.field_image?.field_image?.social?.url } />
        <meta property='og:description' content={ content.field_social_description || content.field_subhead } />
        <link rel='image_src' href={ content?.field_image?.field_image?.social?.url } />
        <meta name='twitter:url' content={ path } />
        <meta name='twitter:image:width' content={ content?.field_image?.field_image?.social?.width } />
        <meta name='twitter:image:height' content={ content?.field_image?.field_image?.social?.height } />
        <meta name='twitter:image' content={ content?.field_image?.field_image?.social?.url } />
        <meta property='article:published_time' content={ content.field_display_date } />
        <meta name='keywords' content={ keywords || SiteSettings.defaultKeywords } />
        <meta name='robots' content={`${content.field_is_promotional ? 'noindex' : 'index'}, max-image-preview:large`} />
      </Head>
      { !isAmp &&
        <Head>
          <script
            key='json-ld'
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
          />
        </Head>
      }
      {/* AMP */}
      { isAmp &&
        <ArticleAmp
          drupalSettings={ drupalSettings }
          data={ data }
          pageType='article'
          siteSection='from the show'
          keywords={ keywords || SiteSettings.defaultKeywords }
        />
      }
      {/* Non-AMP */}
      { !isAmp &&
        <>
          <div className='content'>
            {/* Main Image */}
            {!content.field_video &&
              <ParallaxHeader>
                <div className='content__header-img'>
                  { content?.field_image?.field_image &&
                    <picture>
                      <source media='(max-width: 375px)' srcSet={ content.field_image.field_image.sm.url } />
                      <img
                        src={ content.field_image.field_image.lg.url }
                        alt={ content.field_image?.alt }
                        width={ content.field_image.field_image.lg.width }
                        height={ content.field_image.field_image.lg.height }
                        className='img-full'
                        loading='lazy'
                      />
                    </picture>
                  }
                  { !content?.field_image?.field_image &&
                    <div className='img-missing'>No Image Available</div>
                  }
                </div>
              </ParallaxHeader>
            }
            {/* Main Video */}
            {content.field_video && has(content, 'field_video.field_video_mpx_id.hls_streaming_url') &&
              <>
                <InjectedStylesCatcher />
                <div className='content__header-video'>
                  <div className='content__header-video-wrap'>
                    <Stick>
                      <VideoPlayerWithStore
                        pageType='article'
                        section='article'
                        parentNid={ content.nid }
                        content={ content }
                        drupalSettings={ drupalSettings }
                        playlist={ playlist }
                      />
                    </Stick>
                  </div>
                </div>
                { playlist?.length > 1 &&
                  <CarouselRecomendations playlist={ playlist } />
                }
              </>
            }
            <div className='site-bg'>
              <div className='constrain constrain--bg'>
                <div className='content__share content__share--interior desktop-only'>
                  <Share title={ content.title } />
                </div>
                {/* Title */}
                <h1 className='content__title'>
                  { content.title }
                </h1>
                {/* Is Promotional Flag */}
                { content.field_is_promotional && content.field_flag &&
                  <div className='content__flag'>
                    { content.field_flag }
                  </div>
                }
                <Category content={content} />
                <div className='content__body'>
                  {/* Byline */}
                  { content.field_byline_text &&
                    <div className='content__byline'>
                      By: { content.field_byline_text }
                      <div className='content__byline-date'>
                        { getDate(content.field_display_date) }
                      </div>
                    </div>
                  }
                  {/* Skimlinks */}
                  { content.field_skimlinks &&
                    <Skimlinks
                      type={ content.__typename }
                      data={ drupalSettings }
                    />
                  }
                  {/* Trackonomics */}
                  { content.field_trackonomics &&
                    <Trackonomics />
                  }
                  {/* Subhead */}
                  { content.field_subhead &&
                    <h2>
                      { content.field_subhead }
                    </h2>
                  }
                  {/* Body */}
                  { content.body &&
                    <div className='content__body-main'>
                      <BodyParser
                        content={ content.body }
                        articleNodeId={ content.nid }
                        baseDomain={ fileUrl }
                        drupalSettings={ drupalSettings }
                      />
                    </div>
                  }
                  <div className='content__spacer'></div>
                  <TaxLinks content={ content } />
                  <div className='content__spacer'></div>
                  {/* Related Content */}
                  { content.field_related_content && content.field_related_content.length > 0 &&
                    <div className='content__related'>
                      <h3>related</h3>
                      <ul>
                        { content.field_related_content.map((related) => (
                          <li key={`tag-${key(related)}`}>
                            { related.path &&
                              <>
                                <ReactSVG src={ getIcon(related.__typename) } />
                                <Link legacyBehavior href={ related.path }>
                                  { related.title }
                                </Link>
                              </>
                            }
                          </li>
                        )) }
                      </ul>
                      <img
                        src='/img/drew_heart_related.png'
                        width='40'
                        height='42'
                        alt=''
                        className='content__related-heart'
                        loading='lazy'
                      />
                    </div>
                  }
                  <div className='content__spacer'></div>
                </div>
              </div>
            </div>
          </div>
          { blockItems &&
            <BlockParser blockItems={ blockItems } />
          }
          <div data-ad-ref='leader-plus-flex-bottom' className='bidbarrel-ad'></div>
          <div data-ad-ref='mobile-plus-flex-bottom' className='bidbarrel-ad'></div>
          <Utag
            pageType='article'
            siteSection={ content?.field_category?.name }
            data={{
              sitePrimaryRsid,
              assetType: 'article',
              editorialContentName: content.title,
              editorialContentId: content.nid,
              ...(keywords && {keywords})
            }}
          />
          <BidBarrelReact
            data={ content }
            mapToAdUnits={{
              mobile: [
                'mobile-plus-flex-top',
                'mobile-plus-flex-middle',
                'mobile-plus-flex-bottom'
              ],
              desktop: [
                'leader-plus-flex-top',
                'leader-plus-flex-middle',
                'leader-plus-flex-inc',
                'leader-plus-flex-bottom'
              ]
            }}
          />
        </>
      }
    </>
  );
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/articles/', {
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'article'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  const aliasData = await getAlias({
    alias: `/articles/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Data
  const data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_ARTICLE, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'article_blocks',
    limit: 100
  });

  // Get Site Settings
  const drupalSettings = await getDrupalSettings();

  // Get Recomendations
  const recommendations = await getRecommendations();

  return {
    props: {
      data,
      drupalSettings,
      recommendations,
      blocks
    },
    revalidate: 1
  }
}

export default PageWrapper(Content);
